import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import * as voicifyApi from '../../../api';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import PageError from '../../../components/general/PageError';
import StyledDropdown from '../../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import { OutboundCallFeatureFlagId } from '../../../constants/featureFlagIds';
import { FoodOrderingGenAIModelSettingId } from '../../../constants/featureFlagSettingIds';
import { GenAIModels } from '../../../constants/genAiModels';
import {
    ui, ActionButton, ActivityLog, ButtonRow, EmptyGroup, FieldRow, FooterNote, Group, GroupCaption, GroupFooter,
    GroupSection, GroupTitle, Row, RowDetail, RowText, RowTitle, TextArea
} from './management/ManagementUI';

// Number of applications updated in parallel when applying a setting
const UPDATE_BATCH_SIZE = 5;

const CHECKBOX_OPTIONS = [
    { label: 'Enabled', value: 'true' },
    { label: 'Disabled', value: 'false' }
];

interface BulkFeatureFlagSettingsPanelProps {
    organizationId: string;
    applicationContainer: ApplicationContainer;
    // Locations shown in the Management tab's location list; defaults to every app in the organization
    applications?: ApplicationModel[];
    // The panel works on the Management tab's shared location selection rather than its own list
    selectedAppIds: Set<string>;
    onSelectedAppIdsChange: (selectedAppIds: Set<string>) => void;
}

export const getApplicationFeatureFlagSettingValue = (app: ApplicationModel, featureFlagId: string, featureFlagSettingId: string): string | undefined => {
    // Application settings may carry the schema id as featureFlagSettingId, with id being the record id
    return app?.featureFlags?.find(f => f.id === featureFlagId)?.featureFlagSettings
        ?.find(s => ((s as any).featureFlagSettingId || s.id)?.toLowerCase() === featureFlagSettingId?.toLowerCase())?.value;
};

const formatValue = (value?: string) => value?.length > 0 ? value : 'Not set';

const BulkFeatureFlagSettingsPanel: React.FC<BulkFeatureFlagSettingsPanelProps> = ({ organizationId, applicationContainer, applications, selectedAppIds, onSelectedAppIdsChange }) => {
    const [selectedFeatureFlagId, setSelectedFeatureFlagId] = useState<string>('');
    const [selectedSettingId, setSelectedSettingId] = useState<string>('');
    const [newValue, setNewValue] = useState<string>('');
    const [showCurrentValues, setShowCurrentValues] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        applicationContainer.loadFeatureFlags();
    }, [organizationId]);

    const orgApps = applications ?? applicationContainer.state.baseOrgApplications ?? [];
    const featureFlagDefinitions = applicationContainer.state.featureFlags ?? [];

    // Only offer feature flags that are enabled on at least one app in this organization
    const availableFeatureFlags = featureFlagDefinitions
        .filter(flag => flag.featureFlagSettings?.length > 0)
        .map(flag => ({
            flag,
            appCount: orgApps.filter(app => app.featureFlags?.some(f => f.id === flag.id)).length
        }))
        .filter(f => f.appCount > 0);

    const selectedFeatureFlag = featureFlagDefinitions.find(f => f.id === selectedFeatureFlagId);
    const settingOptions = [...(selectedFeatureFlag?.featureFlagSettings ?? [])].sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0));
    const selectedSetting = settingOptions.find(s => s.id === selectedSettingId);
    const isDropdownSetting = selectedSetting?.type === 'checkbox' || selectedSetting?.id === FoodOrderingGenAIModelSettingId;
    // Text settings may be intentionally cleared, but dropdown settings need one of their options
    const hasValidValue = !!selectedSetting && (!isDropdownSetting || newValue !== '');

    // Default to Outbound Calling when it is available, otherwise the first available flag
    useEffect(() => {
        if (selectedFeatureFlagId && availableFeatureFlags.some(f => f.flag.id === selectedFeatureFlagId)) return;
        const defaultFlag = availableFeatureFlags.find(f => f.flag.id === OutboundCallFeatureFlagId) ?? availableFeatureFlags[0];
        if (defaultFlag) {
            handleFeatureFlagChange(defaultFlag.flag.id);
        }
    }, [availableFeatureFlags.map(f => f.flag.id).join(',')]);

    const eligibleApps = orgApps.filter(app => app.featureFlags?.some(f => f.id === selectedFeatureFlagId));
    const selectedEligibleApps = eligibleApps.filter(app => selectedAppIds.has(app.id));
    const selectedCount = orgApps.filter(app => selectedAppIds.has(app.id)).length;
    const skippedCount = selectedCount - selectedEligibleApps.length;
    const flagName = selectedFeatureFlag?.name ?? 'this feature';

    const resetResults = () => {
        setErrors([]);
        setSuccessMessage(null);
    };

    const handleFeatureFlagChange = (featureFlagId: string) => {
        const flag = featureFlagDefinitions.find(f => f.id === featureFlagId);
        const firstSetting = [...(flag?.featureFlagSettings ?? [])].sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0))[0];
        setSelectedFeatureFlagId(featureFlagId);
        setSelectedSettingId(firstSetting?.id ?? '');
        setNewValue('');
        resetResults();
    };

    const handleSettingChange = (settingId: string) => {
        setSelectedSettingId(settingId);
        setNewValue('');
        resetResults();
    };

    const selectEligibleApps = () => {
        onSelectedAppIdsChange(new Set(eligibleApps.map(app => app.id)));
    };

    const selectAppsWithDifferentValue = () => {
        onSelectedAppIdsChange(new Set(eligibleApps
            .filter(app => (getApplicationFeatureFlagSettingValue(app, selectedFeatureFlagId, selectedSettingId) ?? '') !== newValue)
            .map(app => app.id)));
    };

    const applySetting = async () => {
        if (!hasValidValue || selectedEligibleApps.length === 0) return;

        const confirmed = window.confirm(
            `Set "${selectedSetting.name}" to "${newValue}" on ${selectedEligibleApps.length} application(s)? This takes effect immediately.`
        );
        if (!confirmed) return;

        resetResults();
        setIsApplying(true);
        const newLogs: string[] = [`Updating "${selectedSetting.name}" to "${newValue}" on ${selectedEligibleApps.length} application(s)...`];
        setLogs([...newLogs]);

        // Only the selected setting is sent; the API merges it with the app's other settings
        const requestPayload: UpdateFeatureFlagSettings = {
            featureFlagId: selectedFeatureFlagId,
            featureFlagSettings: [{
                id: selectedSetting.id,
                name: selectedSetting.name,
                value: newValue
            }]
        };

        const failedApps: string[] = [];
        for (let i = 0; i < selectedEligibleApps.length; i += UPDATE_BATCH_SIZE) {
            const batch = selectedEligibleApps.slice(i, i + UPDATE_BATCH_SIZE);
            await Promise.all(batch.map(async app => {
                const previousValue = getApplicationFeatureFlagSettingValue(app, selectedFeatureFlagId, selectedSetting.id);
                try {
                    const result = await voicifyApi.updateApplicationFeatureFlagSettings(app.id, requestPayload);
                    if (result.resultType === 'Ok') {
                        newLogs.push(`✓ ${app.name}: "${previousValue ?? ''}" → "${newValue}"`);
                    } else {
                        failedApps.push(app.name);
                        newLogs.push(`✗ ${app.name}: ${result.errors?.join(', ') || 'Update failed'}`);
                    }
                } catch (error) {
                    failedApps.push(app.name);
                    newLogs.push(`✗ ${app.name}: ${error?.toString() ?? 'Update failed'}`);
                }
            }));
            setLogs([...newLogs]);
        }

        // Reload the apps so the current values reflect the update
        await applicationContainer.getBaseApplicationsForOrg(organizationId, true);

        const succeededCount = selectedEligibleApps.length - failedApps.length;
        if (succeededCount > 0) {
            setSuccessMessage(`Updated "${selectedSetting.name}" on ${succeededCount} application(s).`);
        }
        if (failedApps.length > 0) {
            setErrors([`Failed to update ${failedApps.length} application(s): ${failedApps.join(', ')}`]);
        }
        setIsApplying(false);
    };

    const renderValueInput = () => {
        if (!selectedSetting) return null;

        if (isDropdownSetting) {
            return (
                <StyledDropdown
                    fieldName="newValue"
                    placeholder="Select a value"
                    options={selectedSetting.type === 'checkbox' ? CHECKBOX_OPTIONS : GenAIModels}
                    value={newValue}
                    disabled={isApplying}
                    handleOnChange={(fieldName, value) => setNewValue(value as string)}
                />
            );
        }

        return (
            <TextArea
                id="bulk-feature-flag-setting-value"
                rows={2}
                placeholder={selectedSetting.name}
                value={newValue}
                disabled={isApplying}
                onChange={(e) => setNewValue(e.target.value)}
            />
        );
    };

    const renderSelectionSummary = () => {
        if (selectedCount === 0) {
            return `Select locations from the list, or select every location that has ${flagName}.`;
        }
        if (skippedCount > 0) {
            return `${selectedEligibleApps.length} of ${selectedCount} selected locations have ${flagName}. The other ${skippedCount} will be skipped.`;
        }
        return `All ${selectedCount} selected ${selectedCount === 1 ? 'location has' : 'locations have'} ${flagName}.`;
    };

    if (availableFeatureFlags.length === 0) {
        return <EmptyGroup>No locations in this organization have a feature flag with settings enabled.</EmptyGroup>;
    }

    return (
        <>
            <GroupSection>
                <GroupTitle>Setting</GroupTitle>
                <Group>
                    <FieldRow label="Feature">
                        <StyledDropdown
                            fieldName="featureFlagId"
                            options={availableFeatureFlags.map(f => ({ label: `${f.flag.name} (${f.appCount} apps)`, value: f.flag.id }))}
                            value={selectedFeatureFlagId}
                            disabled={isApplying}
                            handleOnChange={(fieldName, value) => handleFeatureFlagChange(value as string)}
                        />
                    </FieldRow>
                    <FieldRow label="Setting" hint={selectedSetting?.description}>
                        <StyledDropdown
                            fieldName="featureFlagSettingId"
                            options={settingOptions.map(s => ({ label: s.name, value: s.id }))}
                            value={selectedSettingId}
                            disabled={isApplying}
                            handleOnChange={(fieldName, value) => handleSettingChange(value as string)}
                        />
                    </FieldRow>
                    {selectedSetting && (
                        <FieldRow label="New Value" htmlFor="bulk-feature-flag-setting-value">
                            {renderValueInput()}
                        </FieldRow>
                    )}
                </Group>
                <GroupCaption>Only this setting changes. Every other setting on each location stays as it is.</GroupCaption>
            </GroupSection>

            <GroupSection>
                <GroupTitle>Apply To</GroupTitle>
                <Group>
                    <Row>
                        <RowText>
                            <RowTitle>{selectedEligibleApps.length} {selectedEligibleApps.length === 1 ? 'location' : 'locations'}</RowTitle>
                            <RowDetail>{renderSelectionSummary()}</RowDetail>
                        </RowText>
                    </Row>
                    <Row>
                        <ButtonRow>
                            <ActionButton
                                text={`Select All ${eligibleApps.length} With ${flagName}`}
                                onClick={selectEligibleApps}
                                disabled={eligibleApps.length === 0 || isApplying}
                            />
                            <ActionButton
                                text="Select Locations With a Different Value"
                                onClick={selectAppsWithDifferentValue}
                                disabled={!selectedSetting || isApplying}
                            />
                        </ButtonRow>
                    </Row>
                    {selectedEligibleApps.length > 0 && (
                        <DisclosureRow type="button" aria-expanded={showCurrentValues} onClick={() => setShowCurrentValues(!showCurrentValues)}>
                            <Chevron expanded={showCurrentValues} viewBox="0 0 8 12" aria-hidden="true">
                                <path d="M1.5 1.5L6 6l-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </Chevron>
                            {showCurrentValues ? 'Hide' : 'Review'} current values
                        </DisclosureRow>
                    )}
                    {showCurrentValues && selectedEligibleApps.length > 0 && (
                        <ValueList>
                            {selectedEligibleApps.map(app => {
                                const currentValue = getApplicationFeatureFlagSettingValue(app, selectedFeatureFlagId, selectedSettingId);
                                const willChange = hasValidValue && (currentValue ?? '') !== newValue;
                                return (
                                    <ValueRow key={app.id}>
                                        <ValueAppName title={app.name}>{app.name}</ValueAppName>
                                        <ValueChange>
                                            <CurrentValue>{formatValue(currentValue)}</CurrentValue>
                                            {willChange && <NewValue>→ {formatValue(newValue)}</NewValue>}
                                        </ValueChange>
                                    </ValueRow>
                                );
                            })}
                        </ValueList>
                    )}
                    <GroupFooter>
                        <FooterNote>Changes take effect immediately.</FooterNote>
                        <ActionButton
                            variant="primary"
                            text={isApplying ? 'Applying…' : `Apply to ${selectedEligibleApps.length} ${selectedEligibleApps.length === 1 ? 'Location' : 'Locations'}`}
                            onClick={applySetting}
                            disabled={!hasValidValue || selectedEligibleApps.length === 0 || isApplying}
                        />
                    </GroupFooter>
                </Group>
            </GroupSection>

            <ActivityLog title="Activity" lines={logs} isRunning={isApplying} onClear={() => setLogs([])} />

            {successMessage && (
                <PageError errors={[successMessage]} type="success" />
            )}
            <PageError errors={errors} />
        </>
    );
};

const DisclosureRow = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 40px;
    padding: 0 16px;
    border: 0;
    border-top: 0.5px solid ${ui.separator};
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: ${ui.accent};
    text-align: left;
    cursor: pointer;

    &:hover {
        background: rgba(118, 118, 128, 0.06);
    }
`;

const Chevron = styled.svg<{ expanded: boolean }>`
    width: 8px;
    height: 12px;
    transform: rotate(${props => props.expanded ? '90deg' : '0deg'});
    transition: transform 0.15s ease;
`;

const ValueList = styled.div`
    max-height: 320px;
    overflow-y: auto;
    padding: 4px 0;
`;

const ValueRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 6px 16px;
    font-size: 13px;
`;

const ValueAppName = styled.span`
    flex: 1;
    min-width: 0;
    color: ${ui.label};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ValueChange = styled.span`
    display: flex;
    gap: 6px;
    max-width: 50%;
    flex-shrink: 0;
    overflow: hidden;
`;

const CurrentValue = styled.span`
    color: ${ui.secondaryLabel};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const NewValue = styled.span`
    color: ${ui.accent};
    font-weight: 500;
    white-space: nowrap;
`;

export default BulkFeatureFlagSettingsPanel;
